import React, { useEffect } from "react";
import styled from "styled-components";
import { SignOutForm } from "../form/SignOutForm";

type Props = {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const SignOutModal: React.FC<Props> = ({ open, onCancel, onConfirm }) => {
    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (open) {
            // Store original body and document element styles
            const body = document.body;
            const documentElement = document.documentElement;

            const originalBodyOverflow = body.style.overflow;
            const originalBodyPosition = body.style.position;
            const originalBodyTop = body.style.top;
            const originalBodyWidth = body.style.width;
            const originalDocumentOverflow = documentElement.style.overflow;

            // Get current scroll position
            const scrollY = window.scrollY;
            const scrollBarWidth = window.innerWidth - documentElement.clientWidth;

            // Apply comprehensive scroll lock
            body.style.overflow = 'hidden';
            body.style.position = 'fixed';
            body.style.top = `-${scrollY}px`;
            body.style.width = '100%';
            body.style.paddingRight = `${scrollBarWidth}px`;
            documentElement.style.overflow = 'hidden';

            // Prevent touch scrolling on iOS - but only outside modal content
            const preventTouchMove = (e: TouchEvent) => {
                // Allow scrolling within modal content
                const target = e.target as Element;
                const modalContent = document.querySelector('[data-signout-modal-content]');
                if (modalContent && modalContent.contains(target)) {
                    return; // Allow scrolling within modal
                }
                e.preventDefault();
            };

            document.addEventListener('touchmove', preventTouchMove, { passive: false });

            // Cleanup function to restore original state
            return () => {
                body.style.overflow = originalBodyOverflow;
                body.style.position = originalBodyPosition;
                body.style.top = originalBodyTop;
                body.style.width = originalBodyWidth;
                body.style.paddingRight = '';
                documentElement.style.overflow = originalDocumentOverflow;

                document.removeEventListener('touchmove', preventTouchMove);

                // Restore scroll position
                window.scrollTo(0, scrollY);
            };
        }
    }, [open]);

    if (!open) return null;

    // Handle background click to close modal
    const handleBackgroundClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onCancel();
        }
    };

    return (
        <Overlay onClick={handleBackgroundClick}>
            <div data-signout-modal-content>
                <SignOutForm onCancel={onCancel} onConfirm={onConfirm} />
            </div>
        </Overlay>
    );
};

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`; 